import React, { cloneElement, useCallback, useEffect, useMemo, useState } from 'react'
import Chat from '.'
import PastChats from './past-chats'
import NoteInfo from '../note-info'
import Modal from '../modal'
import { deleteChatHistoryAPI, getPastChatsAPI } from '../../common/services'
import { useToaster } from '../../common/contexts/toast'

const ChatWithPastChat = ({ title = "What would you like to know?", children, houseId, objectId, onAskQuestion, onResetChat, type: chatType = "organization", note, ...others }) => {

    // local states
    const [chatId, setChatId] = useState()
    const [isChatStarted, setIsChatStarted] = useState(false)

    const [pastChats, setPastChats] = useState([])
    const [pastChatLoading, setPastChatLoading] = useState(false)

    //-- modal local states
    const [commonModalData, setCommonModalData] = useState({ title: "", type: "", size: "" })
    const [isModalVisible, setIsModalVisible] = useState(false);
    //-- end

    // hook
    const { showToaster } = useToaster()

    // when clicked on any past chat
    const onClickPastChat = useCallback((pastChatData) => {
        const pastChatId = pastChatData.chatId
        setChatId(pastChatId)
        setIsChatStarted(true)
    }, [])

    // when ask question is clicked
    const handleAskQuestion = useCallback((question) => {
        setIsChatStarted(true)
        onAskQuestion && onAskQuestion(question)
    }, [onAskQuestion])

    const placeholderText = useMemo(() => {
        if (objectId) {
            return "Ask the document about the selected element"
        } else if (isChatStarted) {
            return "Ask a question"
        } else {
            return "Ask anything"
        }
    }, [objectId, isChatStarted])

    useEffect(() => {
        // when objectId changes, we are resetting the chat that means chat will go to initial state
        setIsChatStarted(false)
    }, [objectId])

    // Past chat functions
    const getPastChat = useCallback(async () => {
        try {
            setPastChatLoading(true)

            let pastChatAPIData = {
                type: chatType
            }

            if (houseId) {
                pastChatAPIData.houseId = houseId
            }

            if (objectId) {
                pastChatAPIData.objectId = objectId?.toString()
            }

            const pastChatRes = await getPastChatsAPI(pastChatAPIData)
            const allPastchats = pastChatRes.data.chats;
            setPastChats(allPastchats)
        } catch (error) {
            console.log("Error while fetching past chats");
        } finally {
            setPastChatLoading(false)
        }
    }, [houseId, objectId, chatType])

    // when reset chat is clicked
    const handleResetChat = useCallback(() => {
        setIsChatStarted(false)
        setChatId(null)
        getPastChat()
        onResetChat && onResetChat()
    }, [onResetChat, getPastChat])

    // to fetch past chat on initial load
    useEffect(() => {
        getPastChat()
    }, [getPastChat])

    const onDeletePastChat = useCallback((chatData) => {
        setCommonModalData({
            title: "Delete chat",
            size: "medium",
            type: "delete-chat",
            confirmButtonName: "DELETE",
            cancelButtonName: "CANCEL",
            message: <>{`You are about to delete the chat ${chatData?.title || ''}`} <br/> <br/> {"Note, this chat is currently available for your organization team on Bimmatch."} </>,
            chatData,
        });
        setIsModalVisible(true);
    }, [])

    const onClickConfirmModal = useCallback(async (type) => {

        // deleting selected chat
        if (type === "delete-chat") {
            try {
                const toDeleteChatId = commonModalData.chatData.chatId
                setCommonModalData(oldModalData => ({ ...oldModalData, confirmButtonLoading: true }))
                
                const deleteRes = await deleteChatHistoryAPI({ chatId: toDeleteChatId })

                setPastChats(pastChats => pastChats.filter((pastChat) => pastChat.chatId !== toDeleteChatId ))

                // if chat has been deleted from open chat screen then reset the data
                if (isChatStarted) {
                    commonModalData.chatData.resetChat()
                }
                showToaster(`chat-toast`, deleteRes?.data?.message, true)
            } catch (error) {
                showToaster(`chat-toast`, error?.response?.data?.message || "Something went wrong", false)
                console.log("Error while deleting Chat", error)
            } finally {
                setCommonModalData({})
                setIsModalVisible(false)
            }
        }

    }, [commonModalData, isChatStarted, showToaster])

    // here reset chat is function from inside chat comp passed as arg to clear inside data too
    const onDeleteChat = useCallback((resetChat) => {
        onDeletePastChat({ chatId, resetChat })
    }, [onDeletePastChat, chatId])

    return (
        <>
            {(!isChatStarted && !objectId && note) && (
                <>
                    <NoteInfo note={note} withInfoIcon />
                    <hr className='separator' />
                </>
            )}
            {(!isChatStarted && !objectId) && <p className='ask-title'>{title}</p>}
                <Chat
                    id={chatId}
                    objectId={objectId}
                    houseId={houseId}
                    onAskQuestion={handleAskQuestion}
                    onResetChat={handleResetChat}
                    type={chatType}
                    placeholder={placeholderText}
                    onClickDelete={onDeleteChat}
                    {...others}
                />
                {(!isChatStarted) && (
                    <div className="past-chats-wrapper">
                        <PastChats
                            objectId={objectId}
                            houseId={houseId}
                            onClickChat={onClickPastChat}
                            type={chatType}
                            loading={pastChatLoading}
                            onClickDelete={onDeletePastChat}
                            data={pastChats}
                        />
                    </div>
                )}

            {/* Note: Currently Docs list comes here and only visible when no chat has started */}
            {children ? cloneElement(children, { isChatStarted, chatId }) : <></>}

            <Modal
                size={"medium"}
                visible={isModalVisible}
                title={commonModalData?.title}
                modalClass={commonModalData?.type}
                confirmButtonName={commonModalData?.confirmButtonName}
                cancelButtonName={commonModalData?.cancelButtonName}
                onClickCancel={() => setIsModalVisible(false)}
                onClickConfirm={() => onClickConfirmModal(commonModalData.type)}
                onClose={() => setIsModalVisible(false)}
                extraMessage={commonModalData?.message}
                confirmButtonLoading={commonModalData?.confirmButtonLoading}
                confirmButtonDisable={commonModalData?.confirmButtonLoading}
                type={commonModalData?.modalContentType || "surprised"}
            />
        </>
    )
}

export default ChatWithPastChat